'use strict';

const $ = jQuery.noConflict();

/**
 * YouTube Players - keeps only one YouTube embed playing per page.
 *
 * An embed is a cross-origin iframe, so nothing inside it is visible to us. The way in
 * is the YouTube IFrame Player API: given "enablejsapi=1" in the embed url it hands out
 * a player object per iframe that reports playback state and takes commands. The whole
 * feature is then one rule - when a player reports PLAYING, call pauseVideo() on the
 * others. Pausing keeps the position, unlike reloading the iframe.
 *
 * The rest of the file is about finding the iframes, which do not all exist upfront:
 * ajax listings, fancybox popups and slider clones bring in more.
 *
 *   scan()          - marks the theme's iframes (data-yt-bound) and asks for the API
 *   whenApiReady()  - loads the API once, only on pages that have embeds
 *   createPlayers() - attaches a player, kept on the element as iframe.ytPlayer
 *   pauseAll()      - the rule above
 *   observeDom()    - re-scans on DOM changes, so late iframes are picked up
 *   release()       - clears markers of iframes leaving the DOM, so content that is
 *                     moved (fancybox) gets a fresh player instead of a dead one
 *
 * Only the theme's own embeds are claimed - YouTube Feed Pro and Social Wall drive
 * their own players and must not be taken over.
 */
const YouTubePlayers = {
	apiSrc: 'https://www.youtube.com/iframe_api',

	// the theme's embeds, all rendered with "enablejsapi=1" - see
	// BlockHelpers::getYouTubeEmbedUrlFromVideoLink and parts/card/card.js
	selector: '.hero-single__video-iframe, .card__video-iframe, .video-popup__video-iframe',

	// matches both youtube.com/embed/ and the cookieless youtube-nocookie.com/embed/
	embedPattern: /youtube(?:-nocookie)?\.com\/embed\//i,

	apiRequested: false,
	rescanTimer: null,
	idCounter: 0,

	init() {
		// ajax listings bring in new iframes, fancybox sets the popup src on open
		$(document).on('media-listing:updated afterShow.fb', () => this.scheduleScan());

		// a popup video should not keep playing once the popup is gone
		$(document).on('beforeClose.fb', (e, instance) => {
			this.pauseAll(instance && instance.$refs ? instance.$refs.container[0] : null);
		});

		this.observeDom();
		this.scan();
	},

	/**
	 * @description Follow iframes that arrive, leave or get their src set after load -
	 * slider clones, ajax listings, and fancybox moving popup content in and out.
	 */
	observeDom() {
		const observer = new window.MutationObserver((mutations) => {
			let hasNewNodes = false;

			mutations.forEach((mutation) => {
				mutation.removedNodes.forEach((node) => this.release(node));

				if (mutation.addedNodes.length || mutation.type === 'attributes') {
					hasNewNodes = true;
				}
			});

			if (hasNewNodes) {
				this.scheduleScan();
			}
		});

		observer.observe(document.body, {
			childList: true,
			subtree: true,
			attributes: true,
			attributeFilter: ['src'],
		});
	},

	/**
	 * @description Debounce scans - a single slider init can fire dozens of mutations.
	 */
	scheduleScan() {
		window.clearTimeout(this.rescanTimer);
		this.rescanTimer = window.setTimeout(() => this.scan(), 100);
	},

	/**
	 * @description Mark the theme's YouTube iframes that are not marked yet.
	 */
	scan() {
		let found = false;

		document.querySelectorAll(this.selector).forEach((iframe) => {
			// a slick clone copies the markers and the id, but not the player object -
			// start such a copy over so it gets a player of its own
			if (iframe.dataset.ytRegistered && !iframe.ytPlayer) {
				delete iframe.dataset.ytBound;
				delete iframe.dataset.ytRegistered;
				iframe.id = '';
			}

			if (iframe.dataset.ytBound || !this.embedPattern.test(iframe.src || '')) {
				return;
			}

			iframe.dataset.ytBound = '1';

			// an id is not required by the API, but it makes players readable in devtools
			if (!iframe.id) {
				this.idCounter += 1;
				iframe.id = `yt-player-${this.idCounter}`;
			}

			found = true;
		});

		if (found) {
			this.whenApiReady();
		}
	},

	/**
	 * @description Register the marked iframes as soon as the API can be talked to.
	 */
	whenApiReady() {
		// YT.ready() is the API's own callback queue and the only safe hand-off here:
		// YouTube Feed Pro and Social Wall both assign window.onYouTubeIframeAPIReady
		// outright, so whoever loads last wins that global
		if (window.YT && window.YT.ready) {
			window.YT.ready(() => this.createPlayers());
			return;
		}

		if (this.apiRequested) {
			return;
		}

		this.apiRequested = true;

		// the loader guards itself with YT.loading, so a second tag is harmless
		const script = document.createElement('script');
		script.src = this.apiSrc;
		script.async = true;
		document.head.appendChild(script);

		// YT.ready() only exists once the loader has run - wait for it to appear
		const timer = window.setInterval(() => {
			if (window.YT && window.YT.ready) {
				window.clearInterval(timer);
				window.YT.ready(() => this.createPlayers());
			}
		}, 50);
	},

	/**
	 * @description Attach a player to every marked iframe that does not have one.
	 */
	createPlayers() {
		document.querySelectorAll('iframe[data-yt-bound]:not([data-yt-registered])').forEach((iframe) => {
			iframe.dataset.ytRegistered = '1';

			try {
				iframe.ytPlayer = new window.YT.Player(iframe, {
					events: {
						onStateChange: (event) => {
							if (event.data === window.YT.PlayerState.PLAYING) {
								this.pauseAll(document.body, event.target.getIframe());
							}
						},
					},
				});
			} catch (error) {
				// one embed the API refuses must not stop the rest of the batch
				window.console.warn('YouTubePlayers: could not attach player', iframe.id, error);
			}
		});
	},

	/**
	 * @description Pause the players inside a container - the whole page when a video
	 * starts, or a single popup when it closes.
	 * @param {HTMLElement|null} container
	 * @param {HTMLIFrameElement} [except] - the iframe that should keep playing
	 */
	pauseAll(container, except) {
		if (!container) {
			return;
		}

		container.querySelectorAll('iframe[data-yt-registered]').forEach((iframe) => {
			// a player stays unusable until the API answers it, and then it has
			// nothing playing to pause anyway
			if (iframe !== except && iframe.ytPlayer && typeof iframe.ytPlayer.pauseVideo === 'function') {
				iframe.ytPlayer.pauseVideo();
			}
		});
	},

	/**
	 * @description Drop the markers of iframes leaving the DOM, so content that comes
	 * back - fancybox moves the popup out of the card and back - is registered again
	 * against its reloaded player. destroy() is deliberately not called: it deletes
	 * the iframe, which would leave fancybox nothing to put back.
	 * @param {Node} node
	 */
	release(node) {
		if (node.nodeType !== 1) {
			return;
		}

		const iframes = node.matches('iframe[data-yt-bound]')
			? [node]
			: node.querySelectorAll('iframe[data-yt-bound]');

		iframes.forEach((iframe) => {
			iframe.ytPlayer = null;
			delete iframe.dataset.ytBound;
			delete iframe.dataset.ytRegistered;
		});
	},
};

export default YouTubePlayers;
